import { fg } from '@confluence/feature-gating';

interface NetworkCallStats {
	totalOperations: number;
}

interface DetailedNetworkCallStats extends NetworkCallStats {
	byBoundary: Record<string, number>;
}

declare global {
	interface Window {
		__CONFLUENCE_CURRENT_DATA_FETCHER_BOUNDARY__?: string;
	}
}

const NO_DATA_FETCHER_BOUNDARY = 'no-data-fetcher-boundary';

const sanitizeBoundaryName = (value: string): string => value.replace(/[^\-.\w]/g, '_');

const createEmptyStats = (): DetailedNetworkCallStats => ({
	totalOperations: 0,
	byBoundary: {},
});

const cloneStats = (stats: DetailedNetworkCallStats): DetailedNetworkCallStats => ({
	totalOperations: stats.totalOperations,
	byBoundary: { ...stats.byBoundary },
});

const subtractStats = (
	stats: DetailedNetworkCallStats,
	baseline: DetailedNetworkCallStats,
): DetailedNetworkCallStats => {
	const result: DetailedNetworkCallStats = {
		totalOperations: stats.totalOperations - baseline.totalOperations,
		byBoundary: {},
	};
	const boundaryNames = new Set([
		...Object.keys(stats.byBoundary),
		...Object.keys(baseline.byBoundary),
	]);

	boundaryNames.forEach((boundaryName) => {
		const boundaryDelta =
			(stats.byBoundary[boundaryName] ?? 0) - (baseline.byBoundary[boundaryName] ?? 0);

		if (boundaryDelta) {
			result.byBoundary[boundaryName] = boundaryDelta;
		}
	});

	return result;
};

class FrontendNetworkTracker {
	private stats: DetailedNetworkCallStats = createEmptyStats();

	private pageLoadBaseline: DetailedNetworkCallStats = createEmptyStats();

	private getBoundaryName(): string {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		const boundaryName = window.__CONFLUENCE_CURRENT_DATA_FETCHER_BOUNDARY__;

		if (!boundaryName) {
			return NO_DATA_FETCHER_BOUNDARY;
		}

		return sanitizeBoundaryName(boundaryName);
	}

	private trackOperation(): void {
		this.stats.totalOperations++;

		if (!fg('confluence_ufo_api_attribution_metrics')) {
			return;
		}

		const boundaryName = this.getBoundaryName();
		this.stats.byBoundary[boundaryName] = (this.stats.byBoundary[boundaryName] ?? 0) + 1;
	}

	// Called from createWrappedFetch for each individual operation
	trackGraphQLOperations(operationNames: string[]): void {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR || typeof window === 'undefined') {
			return;
		}

		if (!operationNames.length) {
			return;
		}

		operationNames.forEach(() => {
			this.trackOperation();
		});
	}

	// Called from cfetch for REST calls
	trackRestCall(_url?: string): void {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR || typeof window === 'undefined') {
			return;
		}

		this.trackOperation();
	}

	getStats(): NetworkCallStats {
		return { totalOperations: this.stats.totalOperations };
	}

	getDetailedStats(): DetailedNetworkCallStats {
		return cloneStats(this.stats);
	}

	startPageLoadTracking({ isInitial }: { isInitial?: boolean } = {}): void {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR || typeof window === 'undefined') {
			return;
		}

		this.pageLoadBaseline = isInitial ? createEmptyStats() : cloneStats(this.stats);
	}

	getStatsSincePageLoadStart(): DetailedNetworkCallStats {
		return subtractStats(this.stats, this.pageLoadBaseline);
	}

	reset(): void {
		this.stats = createEmptyStats();
		this.pageLoadBaseline = createEmptyStats();
	}
}

// Export singleton instance
export const networkTracker: FrontendNetworkTracker = new FrontendNetworkTracker();
export type { DetailedNetworkCallStats, NetworkCallStats };
